import React, { createContext, useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import type { Tenant, Branch, CardColor } from "./types";
import { MOCK_TENANTS, MOCK_BRANCHES, ALL_CARD_COLORS, ALL_AVATAR_STYLES, ALL_HOBBIES } from "./mock-data";
import { tenantService } from "@/services";
import { hexToHsl } from "@/lib/color";

/** Accept either an HSL triplet ("220 75% 45%") or a hex ("#3b82f6") and return an HSL triplet. */
function normalizePrimaryColor(raw: string | null | undefined): string {
  const v = (raw || "").trim();
  if (!v) return "220 75% 45%";
  if (/^#?[a-f\d]{6}$/i.test(v)) return hexToHsl(v.startsWith("#") ? v : `#${v}`);
  return v;
}

interface TenantContextValue {
  tenant: Tenant | null;
  branches: Branch[];
  loading: boolean;
  setTenantBySlug: (slug: string) => void;
  refreshTenant: () => void;
}

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  branches: [],
  loading: true,
  setTenantBySlug: () => {},
  refreshTenant: () => {},
});

export const useTenant = () => useContext(TenantContext);

/**
 * Map a DB tenant row (+ config JSON) to the front-end Tenant type.
 * Falls back to mock/default values for any missing config keys.
 */
export function mapDbTenantToTenant(row: any): Tenant {
  const c = (row.config || {}) as Record<string, any>;

  // Filter styles/colors to only those enabled
  const enabledStyleIds: string[] = c.enabledStyles || ALL_AVATAR_STYLES.map((s) => s.id);
  const enabledColorIds: string[] = c.enabledColors || ALL_CARD_COLORS.map((col) => col.id);

  // Preserve tenant config order by mapping from enabledStyleIds
  const avatarStyles = enabledStyleIds
    .map((id: string) => ALL_AVATAR_STYLES.find((s) => s.id === id))
    .filter(Boolean) as typeof ALL_AVATAR_STYLES;
  const enabledSystem = ALL_CARD_COLORS.filter((col) => enabledColorIds.includes(col.id));
  // Defensively validate tenant-defined custom colors so a corrupted JSON
  // blob can't break the consumer picker. Each entry must have a
  // "custom-"-prefixed id plus non-empty name/hsl strings.
  const rawCustom = Array.isArray(c.customCardColors) ? c.customCardColors : [];
  const customColors: CardColor[] = rawCustom.filter(
    (col: any): col is CardColor =>
      col &&
      typeof col.id === "string" && col.id.startsWith("custom-") &&
      typeof col.name === "string" && col.name.trim().length > 0 &&
      typeof col.hsl === "string" && col.hsl.trim().length > 0
  );
  const cardColors = [...enabledSystem, ...customColors];

  // Use mock tenant as a source of defaults for complex nested objects
  const mockFallback = Object.values(MOCK_TENANTS)[0];

  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    brandName: row.brand_name || row.name,
    tenantType: (row.tenant_type as "bank" | "waitlist") || "bank",
    primaryColor: normalizePrimaryColor(row.primary_color),
    logoUrl: row.logo_url || "",
    freeAttemptsPerUser: c.freeAttemptsPerUser ?? 5,
    overagePrice: c.overagePrice ?? 2.99,
    billingMode: c.billingMode || "bank_billed",
    notificationEmail: c.notificationEmail || "",
    landingPage: c.landingPage || {
      heroTitle: "Your Card. Your Style. Your Avatar.",
      heroSubtitle: "Create a personalised {{bankName}} card with an AI-generated avatar or your own photo. Stand out every time you pay.",
      features: [],
      ctaText: "Design Your Card",
      footerText: "Powered by Cardmoji",
    },
    // A2 · POLICIES FAIL VISIBLY. Legal text must never come from mock fixtures — if a
    // tenant has no policies configured, surface empty documents so the policy page can
    // render a visible error instead of silently shipping placeholder legal copy.
    policies: c.policies || { privacyPolicy: "", termsOfService: "", biometricConsent: "" },
    cardColors,
    avatarStyles,
    hobbies: c.enabledHobbies
      ? ALL_HOBBIES.filter((h) => (c.enabledHobbies as string[]).includes(h.id))
      : ALL_HOBBIES,
    allowPhotoOnly: c.allowPhotoOnly ?? true,
    allowedOrientations: c.allowedOrientations || ["landscape", "portrait"],
    aiModel: c.aiModel || "google/gemini-2.5-flash",
    adminEmails: c.adminEmails || [],
    officers: c.officers || [],
    cardLayout: {
      ...(c.cardLayout || mockFallback.cardLayout),
      ...(c.nameSide ? { nameSide: c.nameSide } : {}),
      ...(c.cardNumberSide ? { cardNumberSide: c.cardNumberSide } : {}),
    },
    defaultFulfilment: c.defaultFulfilment || "in_branch",
    printerWebhookUrl: c.printerWebhookUrl || undefined,
    cardTemplateLandscape: c.cardTemplateLandscape || undefined,
    cardTemplatePortrait: c.cardTemplatePortrait || undefined,
    taxonomySubStyleOverrides: c.taxonomySubStyleOverrides || c.taxonomyOverrides || [],
    taxonomyHobbyOverrides: c.taxonomyHobbyOverrides || c.taxonomyOverrides || [],
    taxonomyDepthOverrides: c.taxonomyDepthOverrides || {},
    consumerPricing: c.consumerPricing || [],
    // NO FALLBACK PRICE: unconfigured stays undefined so screens refuse loudly.
    cardFeeCents: Number.isFinite(Number(c.cardFeeCents)) ? Number(c.cardFeeCents) : undefined,
    fundingAmountsCents: c.fundingAmountsCents || [2500, 5000, 10000, 25000],
    panDigitsRequired: c.panDigitsRequired ?? 4,
    cardNetwork: (row.card_network as Tenant["cardNetwork"]) || "visa",
    showChip: c.showChip ?? true,
    showCardNetwork: c.showCardNetwork ?? true,
  };
}

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadedSlug, setLoadedSlug] = useState<string | null>(null);

  const setTenantBySlug = async (slug: string, force = false) => {
    if (!force && loadedSlug === slug && tenant) return; // already loaded
    setLoading(true);
    setLoadedSlug(slug);

    try {
      const { data: raw, error } = await tenantService.getTenantBySlug(slug);
      const data = raw as any;

      if (error || !data) {
        console.warn("Tenant not found in DB:", slug);
        if (import.meta.env.DEV) {
          const found = MOCK_TENANTS[slug] || null;
          setTenant(found);
          if (found) {
            setBranches(MOCK_BRANCHES.filter((b) => b.tenantId === found.id));
          } else {
            setBranches([]);
          }
        } else {
          setTenant(null);
          setBranches([]);
        }
        setLoading(false);
        return;
      }

      const mapped = mapDbTenantToTenant(data);
      setTenant(mapped);
      Sentry.setTag("tenant_slug", mapped.slug);
      Sentry.setTag("tenant_id", mapped.id);

      // Load branches from config
      const configBranches = ((data.config as any)?.branches || []) as any[];
      setBranches(
        configBranches.map((b: any) => ({
          id: b.id,
          tenantId: data.id,
          name: b.name || "",
          address: b.address || "",
        }))
      );
    } catch (err) {
      console.error("Error loading tenant:", err);
      if (import.meta.env.DEV) {
        const found = MOCK_TENANTS[slug] || null;
        setTenant(found);
        if (found) {
          setBranches(MOCK_BRANCHES.filter((b) => b.tenantId === found.id));
        } else {
          setBranches([]);
        }
      } else {
        setTenant(null);
        setBranches([]);
      }
    }

    setLoading(false);
  };

  const refreshTenant = () => {
    if (loadedSlug) setTenantBySlug(loadedSlug, true);
  };

  return (
    <TenantContext.Provider value={{ tenant, branches, loading, setTenantBySlug, refreshTenant }}>
      {children}
    </TenantContext.Provider>
  );
};

/** Drop this inside any route with :tenant_slug to auto-load the tenant */
export const TenantLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tenant_slug } = useParams<{ tenant_slug: string }>();
  const { setTenantBySlug } = useTenant();

  useEffect(() => {
    if (tenant_slug) setTenantBySlug(tenant_slug);
  }, [tenant_slug]);

  return <>{children}</>;
};
